import clsx from 'clsx'
import moment from 'moment'
import html2pdf from 'html2pdf.js'
import React from 'react'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import {makeStyles, useTheme} from 'external/material'
import Preview from './Preview'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import useMediaQuery from '@mui/material/useMediaQuery'
import {latinise} from 'helpers/latinise'
import {faFileExport, faSpinner} from '@fortawesome/free-solid-svg-icons'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'

const useStyles = makeStyles(previewStyles)
const useTitleStyles = makeStyles(titleStyles)
const useDialogStyles = makeStyles(dialogStyles)

export default function PreviewBlankPdf ({context}) {

  const [saving, setSaving] = React.useState(false)
  const theme = useTheme()
  const contentRef = React.createRef()
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'))
  const {translate, updateState, quizBlankAssessmentForPdfId: assessmentId, quizBlankAssessmentForPdf: assessment} = context

  const title = (assessment.content && assessment.content.title) || translate('assessment')

  const classes = useStyles()
  const titleClasses = useTitleStyles()
  const dialogClasses = useDialogStyles()

  const isOpen = assessmentId != undefined && assessment.id != undefined && assessmentId == assessment.assessmentId

  function close () {
    updateState({quizBlankAssessmentForPdf: {$set: {}}, quizBlankAssessmentForPdfId: {$set: undefined}})
  }

  async function savePdf() {
    if (!contentRef.current) return
    if (saving) return
    const filename = `${latinise(title).replace(' ', '_')}.pdf`
    setSaving(true)
    const document = await html2pdf()
      .from(contentRef.current)
      .set({
        image: {type: 'png'},
        margin: 0.8,
        jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' },
        pagebreak: {mode: ['css', 'avoid-all']},
      })
      .toPdf()
      .get('pdf')
      .then(processPdf)
      .save(filename)
    setSaving(false)
  }

  function processPdf(pdf) {
    const totalPages = pdf.internal.getNumberOfPages()
    const width =  pdf.internal.pageSize.getWidth()
    const height =  pdf.internal.pageSize.getHeight()


    const addText = (text, x, y, align = 'left', size = 10, color = '#ccc') => {
      pdf.setFontSize(size)
      pdf.setTextColor(color)
      pdf.text(text, x, y, {align: 'right'})
    }

    for (let i = 1; i <= totalPages; i++) {
      pdf.setPage(i)
      addText(`${title}`, width - 0.5, 0.5, 'right', 11, '#000')
      addText(`${i} / ${totalPages}`,  width - 0.5, height - 0.5, 'right');
    }

  }

  return (
    <Dialog
      fullScreen={fullScreen}
      open={isOpen}
      onClose={e => close()}
      classes={dialogClasses}
      aria-labelledby="blank-assessment-pdf-dialog-title"
      maxWidth="lg"
    >
      <DialogTitle id="blank-assessment-pdf-dialog-title" classes={titleClasses}>
        <Box className={classes.title}>{title}</Box>
        <Box className={classes.close}>
          <IconButton onClick={close} size="large">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent className={classes.contentWrapper}>
        <Box className={classes.content} ref={contentRef}>
          <Preview {...{assessment, context}} />
        </Box>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Box onClick={savePdf} className={clsx(classes.button, classes.save)} disabled={saving}>
          {!saving && <><Box className="icon"><PictureAsPdfIcon /></Box><Box className="label">{translate('download')}</Box></>}
          {saving && <div className="icon"><FontAwesomeIcon icon={faSpinner} spin /></div>}
        </Box>
      </DialogActions>
    </Dialog>
  );
}

function dialogStyles(theme) {
  return {
    paper: {
      width: '100%',
      padding: 0,
      height: 'calc(100% - 100px)'
    },
  }
}

function titleStyles(theme) {
  return {
    root: {
      display: 'flex',
      padding: '10px 10px 10px 24px',
      fontSize: '14pt',
      fontWeight: 'bold',
      borderBottom: 'solid 1px #f3f3f3',
      color: '#525b6c',
      background: '#fdfdfd'

    }
  }
}

function previewStyles(theme) {

  const verticalCentering = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center'
  }

  return {
    title: {
      flexGrow: 1,
      ...verticalCentering
    },
    close: {
      ...verticalCentering,
      color: '#69738',
      padding: 0,
      "& svg": {
        fontSize: '14pt'
      }
    },
    button: {
      textDecoration: 'none',
      color: '#fff',
      display: 'flex',
      padding: '7px 15px 7px 12px',
      backgroundColor:'#2ca5ec',
      borderRadius: 3,
      '& >div': {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
      },
      '& .label': {
        fontSize: '13pt'
      },
      cursor: 'pointer',
      '&:hover': {
      },
      '& .icon': {
        marginTop: -1,
        marginRight: 4,
        fontSize: '8pt'
      },
    },
    contentWrapper: {
      marginTop: 5,
    },
    content: {
      display: 'flex',
    },
    actions: {
      padding: 24
    }
  }
}
